/**
 * Installment Pricing DTO for use with {@link PaymentService}.
 * Represents REST response body of shop-service installment pricing endpoint.
 * @property {InstallmentAmountDetails} amount - Details on pricing amount
 * @property {InstallmentAmountDetails} convertedAmount - Details on converted pricing amount
 * @property {Array.<PaymentMethodFee>} paymentMethodFee - List of payment methods, with their available installments
 * @see PaymentService.getInstallmentPricing
 */
class InstallmentPricingDto {
    constructor(data = {}) {
        this.amount = new InstallmentAmountDetails(data.amount);
        this.convertedAmount = new InstallmentAmountDetails(data.convertedAmount);
        this.paymentMethodFee = toClassArray(data.paymentMethodFee, PaymentMethodFee);
    }
}

/**
 * DTO representing total pricing information for {@link InstallmentPricingDto}
 * @property {number} value - Value
 * @property {number} tax - Tax amount
 * @property {number} purchaseValue - Purchase value
 * @property {string} currency - Currency name
 * @see InstallmentPricingDto
 */
class InstallmentAmountDetails {
    constructor(data = {}) {
        this.value = data.value;
        this.tax = data.tax;
        this.purchaseValue = data.purchaseValue;
        this.currency = data.currency;
    }
}

/**
 * DTO containing available installments information for a specific payment method.
 * Part of {@link InstallmentPricingDto}.
 * @property {string} paymentMethod - Payment method name
 * @property {Array.<PricingFee>} pricingFees - List of installments available for this payment method, with their pricing fees
 * @see InstallmentPricingDto
 */
class PaymentMethodFee {
    constructor(data = {}) {
        this.paymentMethod = data.paymentMethod;
        this.pricingFees = toClassArray(data.pricingFees, PricingFee);
    }
}

/**
 * DTO containing pricing fee data for a specific installment count.
 * Part of {@link PaymentMethodFee}.
 * @property {string} installments - String representing installment count
 * @property {PricingData} pricing - Pricing data for this installment count
 * @see PaymentMethodFee
 */
class PricingFee {
    constructor(data = {}) {
        this.installments = data.installments;
        this.pricing = new PricingData(data.pricing);
    }
}

/**
 * DTO representing pricing details specific to a payment method and installment count.
 * Part of {@link PricingFee}.
 * @property {PricingFeeDetail} payerDetail - Payer fee details
 * @property {PricingFeeDetail} merchantDetail - Merchant fee details
 * @property {number} totalValue - Total value
 * @property {number} totalIncomeTransaction - Total income transaction amount
 * @property {PricingAdditionalInfo} additionalInfo - Additional information
 * @see PricingFee
 */
class PricingData {
    constructor(data = {}) {
        this.payerDetail = new PricingFeeDetail(data.payerDetail);
        this.merchantDetail = new PricingFeeDetail(data.merchantDetail);
        this.totalValue = data.totalValue;
        this.totalIncomeTransaction = data.totalIncomeTransaction;
        this.additionalInfo = new PricingAdditionalInfo(data.additionalInfo);
    }
}

/**
 * DTO representing pricing fee breakdown.
 * Part of {@link PricingData}.
 * @property {number} commission - Commission amount
 * @property {number} interests - Interest amount
 * @property {number} tax - Tax amount
 * @property {number} total - Total amount
 * @see PricingData
 */
class PricingFeeDetail {
    constructor(data = {}) {
        this.commission = data.commission;
        this.interests = data.interests;
        this.total = data.total;
    }
}

/**
 * DTO representing additional pricing information.
 * Part of {@link PricingData}.
 * @property {string} cft - CFT
 * @property {string} tea - TEA
 * @see PricingData
 */
class PricingAdditionalInfo {
    constructor(data = {}) {
        this.cft = data.cft;
        this.tea = data.tea;
    }
}

export default InstallmentPricingDto;
export {InstallmentAmountDetails, PaymentMethodFee, PricingFee, PricingData, PricingFeeDetail, PricingAdditionalInfo};


/**
 * Helper function to transform an array of data into an array of class instances
 * @ignore
 * @param {Array.<*>} array - array of input data
 * @param {function} clazz - class constructor
 * @returns {Array.<*>} array of instances of clazz
 */
function toClassArray(array, clazz) {
    if (array instanceof Array) {
        return array.map(el => new clazz(el));
    } else if (array) {
        return [new clazz(array)];
    } else {
        return [];
    }
}
